import { Icon } from '@/libs/components/src/lib/Icon';
import { Icons } from '@/libs/components/src/lib/icons';
import { Tooltip } from '@/libs/components/src/lib/Tooltip';
import { Loading } from '@chiroup/components/Loading';
import React, { cloneElement, isValidElement } from 'react';

type Props = {
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  disabled?: boolean;
  disabledShiftClassName?: string;
  loading?: boolean;
  icon: React.ReactNode | Icons;
  className?: string;
  tooltip?: string;
  ariaLabel?: string;
  type?: 'button' | 'submit' | 'reset';
  badge?: React.ReactNode;
  labelOnlyClassName?: string;
  loadingSize?: number;
  loadingShift?: boolean;
  tooltipClassName?: string;
  asChild?: boolean;
};

type IconElementProps = {
  className?: string;
};

const IconButton: React.FC<Props> = ({
  onClick,
  disabled,
  disabledShiftClassName,
  loading,
  icon,
  className,
  tooltip,
  ariaLabel,
  type = 'button',
  badge,
  labelOnlyClassName,
  loadingSize = 7,
  loadingShift = true,
  tooltipClassName,
  asChild = false,
}) => {
  const onClickStep = (e: any) => {
    onClick?.(e);
  };

  const iconElement = isValidElement<IconElementProps>(icon) ? icon : undefined;
  const isCompleteIcon =
    iconElement &&
    iconElement.type !== React.Fragment &&
    (typeof iconElement.type !== 'string' || iconElement.type === 'svg');

  const iconContent = loading ? (
    <Loading
      className={loadingShift ? '-ml-1' : disabledShiftClassName}
      color="text-gray-500 dark:text-dark-gray-400"
      size={loadingSize}
    />
  ) : typeof icon === 'string' ? (
    <Icon icon={icon as Icons} className={className} />
  ) : isCompleteIcon ? (
    cloneElement(iconElement, {
      className: [
        iconElement.props.className,
        className || (!iconElement.props.className ? 'h-5 w-5' : ''),
      ]
        .filter(Boolean)
        .join(' '),
    })
  ) : (
    <svg
      className={[className ? className : 'h-5 w-5'].join(' ')}
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      {icon}
    </svg>
  );

  if (asChild) {
    return (
      <Tooltip content={tooltip} className={tooltipClassName}>
        <span
          onClick={!disabled && !loading ? onClickStep : undefined}
          className={[
            'relative inline-flex cursor-pointer',
            labelOnlyClassName ? labelOnlyClassName : '',
            disabled || loading
              ? 'cursor-not-allowed text-gray-300 dark:text-dark-gray-200'
              : 'text-gray-500 hover:text-gray-600 dark:text-dark-gray-300 dark:hover:text-dark-gray-200',
          ].join(' ')}
        >
          {iconContent}
          {badge}
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={tooltip} className={tooltipClassName}>
      <label
        className={[
          'relative inline-flex',
          labelOnlyClassName ? labelOnlyClassName : '',
          disabled
            ? 'cursor-not-allowed text-gray-300 dark:text-dark-gray-200'
            : 'text-gray-500 hover:text-gray-600 dark:text-dark-gray-300 dark:hover:text-dark-gray-200',
        ].join(' ')}
        htmlFor="file-upload"
      >
        <button
          type={type}
          aria-label={ariaLabel || tooltip}
          aria-busy={loading || undefined}
          onClick={onClickStep}
          className="icon-button inline-block relative rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-dark-gray-800"
          disabled={disabled || loading}
        >
          {iconContent}
          {badge}
        </button>
      </label>
    </Tooltip>
  );
};

export default IconButton;
